import * as React from 'react';
import { renderToString } from 'react-dom/server';

import { ClassroomInterface, SkillCategoryInterface, StateStandardInterface } from './interfaces';
import { formatGradeLevelWithContext, sortGradeLevels, ALL_GRADE_LEVELS_FOR_DROPDOWN, buildStateStandardOptions, } from './shared'

import { ALL_PRACTICE, CLASS_PRACTICE, } from '../shared/shared'
import { useReportPremiumContext } from '../shared/report_premium_context';
import {
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  SMALL_ICON_BASE_SRC,
  SegmentedControl,
  Tooltip,
  helpIcon,
} from '../../../../Shared/index';

const helpImgSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`;
const lockIconSrc = `${SMALL_ICON_BASE_SRC}/lock.svg`

const ALL_SKILL_CATEGORY_OPTION = { value: null, label: 'All categories'}
const ALL_STUDENTS_OPTION = { value: null, label: 'All students' }
const ALL_GRADES_OPTION = { value: null, label: 'All grades' }

interface StandardsReportSubheaderProps {
  allSkillCategories: SkillCategoryInterface[];
  byStudent: boolean;
  classroom: ClassroomInterface;
  filteredSkillCategories: SkillCategoryInterface[];
  filteredStateStandards: StateStandardInterface[];
  filteredGradeLevels: string[];
  onGradeLevelChange: (grade: any) => void;
  onSkillCategoryChange: (skillCategory: any) => void;
  onStateStandardChange?: (stateStandard: any) => void;
  onStudentChange?: (student: any) => void;
  practiceScope: string;
  selectedGradeLevel: string | null;
  selectedSkillCategoryId: any;
  selectedStateStandardId: any;
  selectedStudentId: any;
  setPracticeScope: (practiceScope: string) => void;
}

const StandardsReportSubheader: React.FC<StandardsReportSubheaderProps> = ({
  allSkillCategories,
  byStudent,
  classroom,
  filteredSkillCategories,
  filteredStateStandards,
  filteredGradeLevels,
  onGradeLevelChange,
  onSkillCategoryChange,
  onStateStandardChange,
  onStudentChange,
  practiceScope,
  selectedGradeLevel,
  selectedSkillCategoryId,
  selectedStateStandardId,
  selectedStudentId,
  setPracticeScope,
}) => {
  const { hasSchoolOrDistrictPremium } = useReportPremiumContext();

  function handleClickPracticeScopeButton(e: React.MouseEvent<HTMLButtonElement>) {
    setPracticeScope(e.currentTarget.value)
  }

  const skillCategoryOptions = React.useMemo(() => [
    byStudent? null : ALL_SKILL_CATEGORY_OPTION,
    ...allSkillCategories
      .map(skillCategory => ({ label: skillCategory.name, value: skillCategory.id, isDisabled: !filteredSkillCategories.find(filteredSkillCategory => skillCategory.id === filteredSkillCategory.id) }))
  ].filter(Boolean), [allSkillCategories, byStudent, filteredSkillCategories])

  const skillCategoryDropdown = (
    <DropdownInput
      className="small bordered"
      disabled={!skillCategoryOptions?.length}
      handleChange={onSkillCategoryChange}
      id="skill-category-dropdown"
      isSearchable={true}
      label='Skill Category'
      labelAdjacentElement={
        (<Tooltip
          tooltipText="Filter by the skill category standards are aligned to. Skill categories with no aligned standards are disabled."
          tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
        />)
      }
      options={skillCategoryOptions}
      placeholder={skillCategoryOptions?.length ? null : 'None available'}
      value={skillCategoryOptions.find(selectedSkillCategory => selectedSkillCategory.value === selectedSkillCategoryId)}
    />
  )

  const stateStandardOptions = React.useMemo(
    () => buildStateStandardOptions(filteredStateStandards, classroom, selectedGradeLevel),
    [filteredStateStandards, selectedGradeLevel, classroom]
  )

  const stateStandardDropdown = onStateStandardChange ? (
    <DropdownInput
      className="small bordered state-standard-dropdown-container"
      disabled={!stateStandardOptions?.length}
      handleChange={onStateStandardChange}
      id="state-standard-dropdown"
      isSearchable={true}
      label='Standard'
      labelAdjacentElement={
        (<Tooltip
          tooltipText="View student performance for a specific standard. Standards that haven’t been practiced yet are disabled."
          tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
        />)
      }
      options={stateStandardOptions}
      placeholder={stateStandardOptions?.length ? null : 'None available'}
      value={stateStandardOptions.find(selectedStateStandard => selectedStateStandard.value === selectedStateStandardId)}
    />
  ) : null

  const studentOptions = React.useMemo(() => [
    ALL_STUDENTS_OPTION,
    ...(classroom?.students?.map(student => ({ label: student.name, value: student.id })) || [])
  ], [classroom?.students])

  const studentDropdown = onStudentChange ? (
    <DropdownInput
      className="small bordered"
      disabled={!studentOptions?.length}
      handleChange={onStudentChange}
      id="student-dropdown"
      isSearchable={true}
      label='Student'
      options={studentOptions}
      placeholder={studentOptions?.length ? null : 'None available'}
      value={studentOptions.find(selectedStudent => selectedStudent.value === selectedStudentId)}
    />
  ) : null

  const gradeLevelOptions = React.useMemo(() => [
    ALL_GRADES_OPTION,
    ...ALL_GRADE_LEVELS_FOR_DROPDOWN
      .map(grade => ({ label: formatGradeLevelWithContext(grade, true), value: grade, isDisabled: !filteredGradeLevels.find(filteredGradeLevel => filteredGradeLevel === grade) }))
      .sort((a, b) => sortGradeLevels(a.value, b.value))
  ], [filteredGradeLevels])


  const gradeLevelDropdown = (
    <DropdownInput
      className="small bordered"
      handleChange={onGradeLevelChange}
      id="grade-level-dropdown"
      isSearchable={true}
      label='Standard Grade Level'
      labelAdjacentElement={
        (<Tooltip
          tooltipText="Filter by the grade level standards are aligned to. Grade levels with no available standards are disabled."
          tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
        />)
      }
      options={gradeLevelOptions}
      value={gradeLevelOptions.find(option => option.value === selectedGradeLevel)}
    />
  )
  
  // TODO - class vs all practice could be shared

  return (
    <div className="subheader">
      <SegmentedControl
        activeTab={practiceScope}
        buttons={[
          {
            label: CLASS_PRACTICE,
            onClick: handleClickPracticeScopeButton,
          },
          {
            label: ALL_PRACTICE,
            labelIcon: hasSchoolOrDistrictPremium ? null : <img alt="Lock" src={lockIconSrc} />,
            onClick: handleClickPracticeScopeButton,
          },
        ]}
        label="Practice Scope"
        labelAdjacentElement={(
          <Tooltip
            tooltipText={renderToString(<React.Fragment>Choose <strong>’All Practice’</strong> to see your students’ complete history, including from other teachers and previous years. Select <strong>’Class Practice’</strong> to focus only on their work in the selected class.</React.Fragment>)}
            tooltipTriggerText={<img alt="Help icon" src={helpImgSrc} />}
          />
        )}
        size="small"
      />
      <div className="filters">
        {gradeLevelDropdown}
        {!byStudent && skillCategoryDropdown}
        {byStudent ? stateStandardDropdown : studentDropdown}
      </div>
    </div>
  );
};

export default StandardsReportSubheader;
